import { useEffect, useRef, useState } from "react";

/**
 * Triggers a CSS reveal animation when the element scrolls into view.
 * Pair with the `.text-reveal` / `.text-reveal.visible` classes from index.css,
 * or any opacity/transform-based class that toggles on `.visible`.
 */
export const useReveal = (options?: {
  fromRight?: boolean;
  threshold?: number;
  rootMargin?: string;
}) => {
  const { fromRight = false, threshold = 0.15, rootMargin = "40px" } = options ?? {};
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.unobserve(el);
        }
      },
      { threshold, rootMargin }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold, rootMargin]);

  const className = `text-reveal ${fromRight ? "from-right" : ""} ${visible ? "visible" : ""}`;
  return { ref, className, visible };
};
